import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { auth, db } from "../firebase";
import {
  collection, collectionGroup, doc, getDoc, getDocs, limit, query, where, Timestamp, orderBy
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { computeRiskFromAdherence } from "../utils/adherenceUtils";

const LS_KEY_GENERIC = "lastClinicId";
const lsKeyForUid = (uid) => "lastClinicId_" + uid;

function toISODate(v) {
  if (!v) return "";
  if (typeof v === "string") return v.length >= 10 ? v.slice(0, 10) : v;
  if (typeof v.toDate === "function") { try { return v.toDate().toISOString().slice(0, 10); } catch {} }
  if (typeof v === "object" && typeof v.seconds === "number") { try { return new Date(v.seconds * 1000).toISOString().slice(0, 10); } catch {} }
  if (typeof v === "number") { try { return new Date(v).toISOString().slice(0, 10); } catch {} }
  try { return String(v).slice(0, 10); } catch { return ""; }
}
function compareByISO(a, b) {
  const aa = toISODate(a) || "";
  const bb = toISODate(b) || "";
  if (aa < bb) return -1;
  if (aa > bb) return 1;
  return 0;
}
function ymdFromDate(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
function startEndOfDayUTC(d) {
  const iso = ymdFromDate(d);
  const start = new Date(`${iso}T00:00:00.000Z`);
  const end = new Date(`${iso}T23:59:59.999Z`);
  return { start: Timestamp.fromDate(start), end: Timestamp.fromDate(end), iso };
}

// ---- Birth year helper (safe) ----
function getBirthYear(obj) {
  const o = obj || {};
  const raw =
    o.birthYear ??
    o.yearOfBirth ??
    o.year_of_birth ??
    o.dob ??
    o.dateOfBirth ??
    o.birth_date ??
    o.birthdate ??
    null;

  if (!raw) return null;
  if (typeof raw === "number") return raw;
  if (typeof raw === "string") {
    const m = raw.match(/\b(19|20)\d{2}\b/);
    return m ? Number(m[0]) : null;
  }
  if (raw?.toDate) {
    try { return raw.toDate().getFullYear(); } catch { return null; }
  }
  return null;
}

// Tiny inline bars
function MiniBars({ series = [], height = 48, title }) {
  const max = Math.max(1, ...series.map(s => Number(s.value || 0)));
  return (
    <div className="rounded-xl border p-3">
      <div className="mb-2 text-xs font-medium text-gray-700">{title}</div>
      <div className="flex items-end gap-1" style={{ height }}>
        {series.map((s, i) => (
          <div key={i}
               className="flex-1 rounded-sm bg-indigo-500/80"
               style={{ height: `${(Number(s.value || 0) / max) * 100}%` }}
               title={`${s.label ?? ""}: ${Number(s.value || 0)}`} />
        ))}
      </div>
      <div className="mt-1 flex justify-between text-[10px] text-gray-500">
        {series.slice(0, 6).map((s, i) => <span key={i}>{s.label ?? ""}</span>)}
      </div>
    </div>
  );
}

// --- Robust med name derivation for Recent Logs ---
function deriveMedName(l) {
  const directFields = [
    "med", "medication", "medName", "name", "drug", "label",
    "rxnormName", "rxName", "rx_title", "title", "displayName",
    "med_key", "medKey", "med_id", "medId"
  ];
  for (const k of directFields) {
    const v = l?.[k];
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  const id = String(l?.id || "");
  if (id) {
    let s = id.replace(/^(\d{4}[-_]?\d{2}[-_]?\d{2})(T.*)?[-_]?/i, "");
    s = s.replace(/\b(undo|redo|taken|missed|log)\b/gi, "");
    const pretty = prettifyKey(s);
    if (pretty) return pretty;
  }
  if (l?.medKey) {
    const pretty = prettifyKey(String(l.medKey));
    if (pretty) return pretty;
  }
  return "—";
}
function prettifyKey(s) {
  if (!s) return "";
  let t = s.replace(/[_\-]+/g, " ").trim();
  t = t.replace(/\b(\d+)\s*(mg|ml)\d+\b/gi, (_, n, u) => `${n} ${u.toLowerCase()}`);
  t = t.replace(/\b(\d+)(mg|ml)\d*\b/gi, (_, n, u) => `${n} ${u.toLowerCase()}`);
  t = t.replace(/\b(x|#)\d+\b/gi, "").replace(/\s{2,}/g, " ").trim();
  t = t.split(" ").map((w) => {
    const lower = w.toLowerCase();
    if (["mg", "ml"].includes(lower)) return lower;
    return w.charAt(0).toUpperCase() + w.slice(1);
  }).join(" ").trim();
  t = t.replace(/^[\W_]+|[\W_]+$/g, "");
  return t;
}

// ---- Fetch exact Taken/Missed lists for a specific day ----
async function fetchTakenMissedForDate({ clinicCode, patientId, date, db }) {
  const { start, end, iso } = startEndOfDayUTC(date);
  const base = ["clinics", clinicCode, "patients", patientId];

  const statusQ = query(
    collection(db, ...base, "daily_med_status"),
    where("date", ">=", start),
    where("date", "<=", end)
  );
  const statusSnap = await getDocs(statusQ);

  const summaryRef = doc(db, ...base, "daily_summaries", iso);
  const summarySnap = await getDoc(summaryRef);
  const summary = summarySnap.exists() ? (summarySnap.data() || {}) : null;

  let taken = [];
  let missed = [];

  if (!statusSnap.empty) {
    statusSnap.forEach((ds) => {
      const d = ds.data() || {};
      const name = (d.medication || d.med || d.name || "").trim();
      const st = (d.status || "").toLowerCase();
      if (!name) return;
      if (st === "taken" || d.taken === true) taken.push(name);
      else missed.push(name);
    });
    if (summary && Array.isArray(summary.missedMeds)) {
      const seen = new Set([...taken, ...missed]);
      summary.missedMeds.forEach((m) => {
        const nm = (m || "").trim();
        if (nm && !seen.has(nm)) missed.push(nm);
      });
    }
  } else if (summary) {
    if (Array.isArray(summary.missedMeds)) missed = summary.missedMeds.filter(Boolean);
  }

  const uniqSort = (arr) => Array.from(new Set(arr)).sort((a, b) => a.localeCompare(b));
  return {
    iso,
    taken: uniqSort(taken),
    missed: uniqSort(missed),
    summary: summary
      ? {
          totalScheduled: Number(summary.totalScheduled || 0),
          taken: Number(summary.taken || 0),
          missed: Number(summary.missed || 0),
          missedMeds: Array.isArray(summary.missedMeds) ? uniqSort(summary.missedMeds) : [],
        }
      : null,
  };
}

// ---------- saved_meds helpers ----------
function stableUniqueMeds(rows) {
  const seen = new Set();
  const out = [];
  for (const r of rows) {
    const key = `${(r.nameLower || String(r.name || r.med || "")).trim().toLowerCase()}|${r.dosageSort ?? r.dosage ?? ""}|${r.rxCui ?? ""}`;
    if (!seen.has(key)) {
      seen.add(key);
      out.push(r);
    }
  }
  return out;
}
function stableSortMeds(rows) {
  return [...rows].sort((a, b) => {
    const an = (a.nameLower || a.name || a.med || "").toString().toLowerCase();
    const bn = (b.nameLower || b.name || b.med || "").toString().toLowerCase();
    if (an < bn) return -1;
    if (an > bn) return 1;
    const ad = a.dosageSort ?? a.dosage ?? 0;
    const bd = b.dosageSort ?? b.dosage ?? 0;
    if (ad < bd) return -1;
    if (ad > bd) return 1;
    const at = a.createdAt?.seconds ?? 0;
    const bt = b.createdAt?.seconds ?? 0;
    return at - bt;
  });
}

export default function Patient() {
  const { patientId } = useParams();
  const [search] = useSearchParams();
  const navigate = useNavigate();

  const urlClinic = (search.get("clinic") || "").toUpperCase();
  const [user, setUser] = useState(auth.currentUser || null);
  const [clinicCode, setClinicCode] = useState(urlClinic || null);
  const [patientMeta, setPatientMeta] = useState(null);

  const [savedMeds, setSavedMeds] = useState([]);
  const [statusDays, setStatusDays] = useState([]);
  const [summaries, setSummaries] = useState([]);
  const [medLogs, setMedLogs] = useState([]);

  const [selectedISO, setSelectedISO] = useState(ymdFromDate(new Date()));
  const [takenList, setTakenList] = useState([]);
  const [missedList, setMissedList] = useState([]);
  const [loadingLists, setLoadingLists] = useState(false);

  const [adherence, setAdherence] = useState({ pct: null, taken: 0, total: 0 });

  useEffect(() => onAuthStateChanged(auth, (u) => setUser(u || null)), []);

  // Resolve clinic if not provided
  useEffect(() => {
    if (urlClinic || !user) return;
    (async () => {
      let resolved = null;
      try {
        const idx = await getDoc(doc(db, "provider_index", user.uid));
        if (idx.exists()) {
          const v = idx.data() || {};
          resolved = (v.clinicId || v.code || "").toString().trim().toUpperCase() || null;
        }
      } catch {}
      if (!resolved) {
        try {
          const cg = await getDocs(query(collectionGroup(db, "providers"), where("uid", "==", user.uid), limit(1)));
          if (cg.docs.length) resolved = (cg.docs[0].ref.path.split("/")[1] || "").toUpperCase();
        } catch {}
      }
      if (!resolved) {
        try {
          resolved = (localStorage.getItem(lsKeyForUid(user.uid)) || "").toUpperCase()
                 || (localStorage.getItem(LS_KEY_GENERIC) || "").toUpperCase()
                 || null;
        } catch {}
      }
      setClinicCode(resolved);
    })();
  }, [user, urlClinic]);

  // Load patient + subcollections
  useEffect(() => {
    if (!clinicCode || !patientId) return;
    (async () => {
      try {
        const pSnap = await getDoc(doc(db, "clinics", clinicCode, "patients", patientId));
        setPatientMeta(pSnap.exists() ? pSnap.data() || null : null);

        let medsDocs;
        try {
          const qy = query(
            collection(db, "clinics", clinicCode, "patients", patientId, "saved_meds"),
            orderBy("nameLower", "asc"),
            orderBy("dosageSort", "asc"),
            orderBy("createdAt", "asc")
          );
          medsDocs = await getDocs(qy);
        } catch {
          const qy = query(
            collection(db, "clinics", clinicCode, "patients", patientId, "saved_meds"),
            orderBy("__name__")
          );
          medsDocs = await getDocs(qy);
        }
        const smedsRaw = medsDocs.docs.map(d => ({ id: d.id, ...(d.data() || {}) }));
        const smeds = stableSortMeds(stableUniqueMeds(smedsRaw));
        setSavedMeds(smeds);

        const dstatus = await getDocs(collection(db, "clinics", clinicCode, "patients", patientId, "daily_med_status"));
        const status = dstatus.docs.map(d => {
          const v = d.data() || {};
          const iso = toISODate(v.date || d.id);
          return { id: d.id, iso, medication: v.medication || v.med, status: v.status, taken: v.taken === true };
        }).sort((a, b) => compareByISO(a.iso, b.iso));
        setStatusDays(status);

        const dsumm = await getDocs(collection(db, "clinics", clinicCode, "patients", patientId, "daily_summaries"));
        const sums = dsumm.docs.map(d => {
          const v = d.data() || {};
          const iso = toISODate(v.date || d.id);
          return { id: d.id, iso, ...v };
        }).sort((a, b) => compareByISO(a.iso, b.iso));
        setSummaries(sums);

        const mlogs = await getDocs(collection(db, "clinics", clinicCode, "patients", patientId, "medLogs"));
        const logs = mlogs.docs.map(d => {
          const v = d.data() || {};
          const sortKey = toISODate(v.timestamp || v.date || d.id);
          const displayName = deriveMedName({ ...v, id: d.id });
          return { id: d.id, sortKey, displayName, ...v };
        }).sort((a, b) => compareByISO(a.sortKey, b.sortKey));
        setMedLogs(logs);
      } catch (e) {
        console.error(e);
      }
    })();
  }, [clinicCode, patientId]);

  // Compute adherence (shared util, 30-day window)
  useEffect(() => {
    if (!clinicCode || !patientId) return;
    let alive = true;
    (async () => {
      try {
        const res = await computeRiskFromAdherence(clinicCode, patientId, 30);
        if (alive) setAdherence({ pct: res.pct, taken: res.taken, total: res.total });
      } catch {
        if (alive) setAdherence({ pct: null, taken: 0, total: 0 });
      }
    })();
    return () => { alive = false; };
  }, [clinicCode, patientId]);

  const kpis = useMemo(() => {
    const last7 = (summaries || []).slice(-7).map(s => ({
      label: (s?.iso || "").slice(5),
      value: Number(s?.taken ?? 0)
    }));
    return { pct: adherence.pct ?? 0, taken: adherence.taken ?? 0, total: adherence.total ?? 0, last7 };
  }, [summaries, adherence]);

  useEffect(() => {
    if (!clinicCode || !patientId || !selectedISO) return;
    (async () => {
      try {
        setLoadingLists(true);
        const parts = selectedISO.split("-");
        const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]), 12, 0, 0);
        const res = await fetchTakenMissedForDate({ clinicCode, patientId, date: d, db });
        setTakenList(res.taken || []);
        setMissedList(res.missed || (res.summary?.missedMeds ?? []));
      } catch (e) {
        console.error(e);
        setTakenList([]);
        setMissedList([]);
      } finally {
        setLoadingLists(false);
      }
    })();
  }, [clinicCode, patientId, selectedISO]);

  function back() {
    navigate(`/dashboard?clinic=${encodeURIComponent(clinicCode || "")}`);
  }

  // ---- Header name + birth year
  const displayFullName = useMemo(() => {
    const first =
      patientMeta?.firstName ??
      patientMeta?.first_name ??
      patientMeta?.given_name ??
      "";
    const last =
      patientMeta?.lastName ??
      patientMeta?.last_name ??
      patientMeta?.family_name ??
      "";
    const fl = `${String(first).trim()} ${String(last).trim()}`.trim();
    return fl || patientMeta?.name || patientMeta?.displayName || patientId;
  }, [patientMeta, patientId]);

  const birthYear = useMemo(() => getBirthYear(patientMeta), [patientMeta]);

  const dayChips = useMemo(() => {
    const days = (summaries?.length
      ? summaries.map(s => ({ iso: s.iso, taken: Number(s.taken || 0), missed: Number(s.missed || 0) }))
      : statusDays.map(s => ({ iso: s.iso, taken: (s.taken === true) || String(s.status).toLowerCase().includes("taken") ? 1 : 0, missed: 0 }))
    ).filter(d => !!d.iso);
    const uniq = new Map();
    for (const d of days) if (d.iso) uniq.set(d.iso, d);
    return Array.from(uniq.values()).sort((a,b) => compareByISO(a.iso, b.iso)).slice(-14);
  }, [summaries, statusDays]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-50/70 via-white to-white">
      <div className="mx-auto max-w-7xl px-5 py-5">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <div className="text-xs text-gray-500">Clinic: <span className="font-mono">{clinicCode || "—"}</span></div>
            <h1 className="text-xl font-semibold text-gray-900">{displayFullName}</h1>
            {birthYear ? (
              <div className="text-xs text-gray-600">Birth year: {birthYear}</div>
            ) : null}
          </div>
          <button onClick={back} className="rounded-full border border-gray-300 px-3 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-50">Back to Dashboard</button>
        </div>

        <div className="grid gap-3 md:grid-cols-3">
          <div className="rounded-xl border bg-white p-3">
            <div className="mb-2 text-xs uppercase tracking-wide text-gray-500">Adherence</div>
            <div className="text-2xl font-semibold">{kpis.pct ?? 0}%</div>
            <div className="text-xs text-gray-500">
              {typeof kpis.taken === "number" && typeof kpis.total === "number"
                ? `${kpis.taken} of ${kpis.total} marked Taken`
                : `Rolling window`}
            </div>
          </div>

          <div className="rounded-xl border bg-white p-3 md:col-span-2">
            <MiniBars title="Daily Summary (last 7)" series={kpis.last7} />
          </div>
        </div>

        <div className="mt-4 grid gap-3 md:grid-cols-2">
          <div className="rounded-xl border bg-white p-3">
            <div className="mb-2 text-xs uppercase tracking-wide text-gray-500">Saved Medications</div>
            {savedMeds.length === 0 ? (
              <div className="text-sm text-gray-500">No saved meds.</div>
            ) : (
              <ul className="text-sm">
                {savedMeds.map(m => (
                  <li key={m.id} className="flex items-center justify-between border-b py-2">
                    <div>
                      <div className="font-medium text-gray-900">{m.name || m.med || m.id}</div>
                      <div className="text-xs text-gray-500">{m.dosage || m.strength || "—"}</div>
                    </div>
                    <span className="text-xs text-gray-500">{m.frequency || "—"}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="rounded-xl border bg-white p-3">
            <div className="mb-2 text-xs uppercase tracking-wide text-gray-500">Recent Med Logs</div>
            {medLogs.length === 0 ? (
              <div className="text-sm text-gray-500">No logs yet.</div>
            ) : (
              <ul className="text-sm">
                {medLogs.slice(-10).reverse().map(l => (
                  <li key={l.id} className="flex items-center justify-between border-b py-2">
                    <div>
                      <div className="font-medium text-gray-900">
                        {(() => {
                          const n = (l.displayName || deriveMedName(l) || "").trim();
                          return n && n !== "—" ? n : (l.med || l.medication || "Unknown medication");
                        })()}
                      </div>
                      <div className="text-xs text-gray-500">
                        {(l.status || l.result || "—").toString()} • {toISODate(l.timestamp || l.date || l.sortKey || l.id)}
                      </div>
                    </div>
                    <span className="text-xs font-mono text-gray-500">
                      {(l.dose || l.dosage) ?? ""}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="mt-4 rounded-xl border bg-white p-3">
          <div className="mb-2 text-xs uppercase tracking-wide text-gray-500">Daily Med Status (last 14)</div>

          {dayChips.length === 0 ? (
            <div className="text-sm text-gray-500">No daily status yet.</div>
          ) : (
            <div className="flex flex-wrap gap-2 mb-3">
              {dayChips.map(d => {
                const isSelected = d.iso === selectedISO;
                const taken = Number(d.taken || 0);
                const missed = Number(d.missed || 0);
                const ok = taken > 0 && missed === 0;
                return (
                  <button
                    key={d.iso}
                    onClick={() => setSelectedISO(d.iso)}
                    className={`rounded-full border px-2 py-1 text-xs font-medium transition ${
                      isSelected
                        ? "border-indigo-500 bg-indigo-50 text-indigo-700"
                        : ok
                        ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                        : "border-rose-300 bg-rose-50 text-rose-700"
                    }`}
                    title={`Taken: ${taken} • Missed: ${missed}`}
                  >
                    {(d.iso || "").slice(5)} • {ok ? "Taken" : missed > 0 ? "Missed" : "—"}
                  </button>
                );
              })}
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-4">
            <div className="rounded-lg border p-3">
              <div className="mb-2 font-medium">Taken — {selectedISO}</div>
              {loadingLists ? (
                <div className="text-sm text-gray-500">Loading…</div>
              ) : takenList.length === 0 ? (
                <div className="text-sm text-gray-500">None recorded.</div>
              ) : (
                <ul className="list-disc pl-5 space-y-1 text-sm">
                  {takenList.map((m) => <li key={`t-${m}`}>{m}</li>)}
                </ul>
              )}
            </div>

            <div className="rounded-lg border p-3">
              <div className="mb-2 font-medium">Missed — {selectedISO}</div>
              {loadingLists ? (
                <div className="text-sm text-gray-500">Loading…</div>
              ) : missedList.length === 0 ? (
                <div className="text-sm text-gray-500">None recorded.</div>
              ) : (
                <ul className="list-disc pl-5 space-y-1 text-sm">
                  {missedList.map((m) => <li key={`m-${m}`}>{m}</li>)}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
